'use client'

import React from 'react'
import { cn } from '../lib/cn'

interface PrismProps {
  className?: string
  size?: number
}

/**
 * Animated prism component with layered triangle design and soft pulsating animation.
 */
export const Prism: React.FC<PrismProps> = ({ className, size = 200 }) => {
  // Original SVG is 310x272
  // Triangle extends from y=38.8 to y=213.5 (height ~175)
  // Base width is from x=48.5 to x=260.7 (width ~212)
  // Center is at x=154.6 (middle of 310)
  
  // Scale factor to map from 272px height to desired size
  const scale = size / 272
  const width = 310 * scale
  const centerX = width * (154.603 / 310) // Original center is at 154.603 in 310 width
  
  // Calculate positions based on actual SVG coordinates scaled proportionally
  // Layer 1: M154.603 38.8119L260.691 213.465H48.5149
  const l1_topY = 38.8119 * scale
  const l1_baseY = 213.465 * scale
  const l1_leftX = 48.5149 * scale
  const l1_rightX = 260.691 * scale
  
  // Layer 2: M154.603 50.9406L241.784 203.762H67.4217
  const l2_topY = 50.9406 * scale
  const l2_baseY = 203.762 * scale
  const l2_leftX = 67.4217 * scale
  const l2_rightX = 241.784 * scale
  
  // Layer 3: M154.603 67.9208L229.18 195.272H80.0262
  const l3_topY = 67.9208 * scale
  const l3_baseY = 195.272 * scale
  const l3_leftX = 80.0262 * scale
  const l3_rightX = 229.18 * scale
  
  // Layer 4: M155.816 84.9012L214.637 184.963H96.9947
  const l4_topY = 84.9012 * scale
  const l4_baseY = 184.963 * scale
  const l4_leftX = 96.9947 * scale
  const l4_rightX = 214.637 * scale
  
  // Layer 5: M157.029 106.733L197.993 175.866H116.064
  const l5_topY = 106.733 * scale
  const l5_baseY = 175.866 * scale
  const l5_leftX = 116.064 * scale
  const l5_rightX = 197.993 * scale
  
  return (
    <div className={cn('relative flex items-center justify-center', className)} style={{ width: `${width}px`, height: `${size}px` }}>
      <div
        className="relative"
        style={{
          width: `${width}px`,
          height: `${size}px`,
        }}
      >
        {/* Layer 1 - Stronger Tea */}
        <svg
          className="absolute"
          width={`${width}`}
          height={`${size}`}
        >
          <path
            d={`M ${centerX},${l1_topY} L ${l1_rightX},${l1_baseY} H ${l1_leftX} Z`}
            fill="#C5ECAC"
          />
        </svg>
        
        {/* Layer 2 - Tiffany */}
        <svg className="absolute" width={`${width}`} height={`${size}`}>
          <path
            d={`M ${centerX},${l2_topY} L ${l2_rightX},${l2_baseY} H ${l2_leftX} Z`}
            fill="#A8E1DF"
          />
        </svg>
        
        {/* Layer 3 - Sky Blue */}
        <svg className="absolute" width={`${width}`} height={`${size}`}>
          <path
            d={`M ${centerX},${l3_topY} L ${l3_rightX},${l3_baseY} H ${l3_leftX} Z`}
            fill="#88C7DB"
          />
        </svg>
        
        {/* Layer 4 - Blue Grey */}
        <svg className="absolute" width={`${width}`} height={`${size}`}>
          <path
            d={`M ${centerX},${l4_topY} L ${l4_rightX},${l4_baseY} H ${l4_leftX} Z`}
            fill="#5D97D1"
          />
        </svg>
        
        {/* Layer 5 - Baby Powder with inner shadow effect */}
        <svg
          className="absolute"
          width={`${width}`}
          height={`${size}`}
        >
          <defs>
            <filter id={`prismInnerShadow-${size}`}>
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="9.7"/>
              <feGaussianBlur stdDeviation="24.3"/>
              <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.866667 0 0 0 0 0.972549 0 0 0 0 0.764706 0 0 0 1 0"
              />
              <feBlend mode="normal" in2="shape" result="effect1_innerShadow"/>
            </filter>
            <linearGradient id={`prismInnerGlow-${size}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(168, 225, 223, 0.3)"/>
              <stop offset="50%" stopColor="rgba(136, 199, 219, 0.4)"/>
              <stop offset="100%" stopColor="rgba(168, 225, 223, 0.3)"/>
            </linearGradient>
          </defs>
          <path
            d={`M ${centerX},${l5_topY} L ${l5_rightX},${l5_baseY} H ${l5_leftX} Z`}
            fill="#FAF9F5"
            filter={`url(#prismInnerShadow-${size})`}
          />
          {/* Inner glow layer */}
          <path
            d={`M ${centerX},${l5_topY} L ${l5_rightX},${l5_baseY} H ${l5_leftX} Z`}
            fill={`url(#prismInnerGlow-${size})`}
            style={{ pointerEvents: 'none' }}
          />
        </svg>
      </div>
    </div>
  )
}
